import React, { useState, useEffect, useRef } from "react";
import { Link } from 'react-router-dom';
import { FaGlobe, FaSearch, FaRobot, FaTimes, FaUniversalAccess, FaSlidersH, FaHandsHelping, FaLaptopCode } from 'react-icons/fa';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import BookingForm from '../components/BookingForm';
import ServiceFlipCard from '../components/ServiceFlipCard';
import { API_ENDPOINTS, submitForm } from '../config/api';

const MONTH_OPTIONS = [
  { value: '01', label: 'January' },
  { value: '02', label: 'February' },
  { value: '03', label: 'March' },
  { value: '04', label: 'April' },
  { value: '05', label: 'May' },
  { value: '06', label: 'June' },
  { value: '07', label: 'July' },
  { value: '08', label: 'August' },
  { value: '09', label: 'September' },
  { value: '10', label: 'October' },
  { value: '11', label: 'November' },
  { value: '12', label: 'December' },
];

const getDaysInMonth = (month, year) => {
  if (!month || !year) return 31;
  return new Date(parseInt(year, 10), parseInt(month, 10), 0).getDate();
};

const getYearOptions = () => {
  const currentYear = new Date().getFullYear();
  return [currentYear, currentYear + 1];
};

export default function HomePage() {
  const [showScrollIndicator, setShowScrollIndicator] = useState(true);
  const [isBookingModalOpen, setIsBookingModalOpen] = useState(false);
  const [bookingStep, setBookingStep] = useState(0);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    name: '',
    email: '',
    phone: '',
    month: '',
    day: '',
    year: '',
    date: '',
    time: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState({ type: '', message: '' });
  const [activeVideoIndex, setActiveVideoIndex] = useState(null);
  const videoRefs = useRef([]);
  const stepInputRef = useRef(null);

  const bookingSteps = [
    { field: 'firstName', emoji: '👋', label: "What's your first name?", placeholder: 'First name', type: 'text', required: true },
    { field: 'lastName', emoji: '✨', label: "What's your last name?", placeholder: 'Last name', type: 'text', required: true },
    { field: 'email', emoji: '📧', label: "What's your email?", placeholder: 'you@example.com', type: 'email', required: true },
    { field: 'phone', emoji: '📱', label: "What's your phone number?", placeholder: '(818) 555-0123', type: 'tel', required: true },
    { field: 'date', emoji: '📅', label: 'Preferred consultation date', placeholder: '', type: 'dateDropdowns', required: true },
    { field: 'time', emoji: '⏰', label: 'Preferred consultation time', placeholder: '', type: 'select', required: true },
    { field: 'message', emoji: '💬', label: 'Anything else you want to share? (Optional)', placeholder: 'Tell us about your project...', type: 'textarea', required: false },
  ];

  const portfolioVideos = [
    { title: 'Restaurant Website Demo', videoSrc: '/restaurantDEMO.mp4' },
    {
      title: 'NEMT Business Demo',
      videoSrc: '/nemtDEMO.mp4',
      description: 'See this full build live with a real, trusted NEMT business.',
      liveUrl: 'https://stattransportnemt.com/',
      liveLabel: 'Visit Stat Transport',
    },
  ];

  const sectionHeadingStyle = {
    fontFamily: "'Poppins', sans-serif",
    fontWeight: 700,
    letterSpacing: '0.05em',
  };

  const services = [
    {
      icon: FaGlobe,
      title: 'Website',
      tagline: 'Turn visitors into leads',
      price: 'Starting at $500',
      outcome: 'A site that looks credible and asks for the sale.',
      description:
        'Custom coded websites built on marketing psychology. Every element is designed to build trust, establish credibility, and convert visitors into leads through strategic forms and calls to action.',
      highlights: ['Looks pro on phone', 'Clear call to action', 'Forms that capture leads'],
      badge: 'Most Popular',
    },
    {
      icon: FaSearch,
      title: 'SEO',
      tagline: 'Get found by people ready to buy',
      price: 'Starting at $200/mo',
      outcome: 'Your business shows up when locals search for what you offer.',
      description:
        'Rank higher on Google and show up locally. We optimize your site, Google Business Profile, and content so the right customers find you.',
      highlights: ['Local search visibility', 'Google Business Profile', 'Keywords that matter'],
    },
    {
      icon: FaRobot,
      title: 'AI Automations',
      tagline: 'Follow up while you sleep',
      price: null,
      outcome: 'Every lead gets a fast, personal reply without you typing every message.',
      description:
        'Smart workflows that follow up with leads, route inquiries, and handle repetitive tasks so you can focus on running your business.',
      highlights: ['Instant follow ups', 'Lead nurturing', 'Less busywork'],
    },
    {
      icon: FaLaptopCode,
      title: 'Customized Software',
      tagline: 'Built specifically for your business',
      price: 'Varies by project',
      outcome: 'One system that runs your workflows instead of spreadsheets and scattered apps.',
      description:
        'Custom software shaped around how your business actually works. From worker ticketing to audit trails, we build tools your team can rely on every day.',
      highlights: ['Worker ticketing', 'Activity audit trails', 'Fits your workflow'],
      seeMoreLink: '/custom-software',
    },
  ];

  const handleVideoClick = (index) => {
    if (activeVideoIndex === index) return;
    if (activeVideoIndex !== null && videoRefs.current[activeVideoIndex]) {
      videoRefs.current[activeVideoIndex].pause();
      videoRefs.current[activeVideoIndex].currentTime = 0;
    }
    setActiveVideoIndex(index);
    const video = videoRefs.current[index];
    if (video) {
      video.play().catch(() => {});
    }
  };

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 100) {
        setShowScrollIndicator(false);
      } else {
        setShowScrollIndicator(true);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (!isBookingModalOpen) return;

    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        setIsBookingModalOpen(false);
      }
    };

    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', handleEscape);

    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', handleEscape);
    };
  }, [isBookingModalOpen]);

  // Occasionally auto-open the booking popup after a short stay
  useEffect(() => {
    const STORAGE_KEY = 'webanova-booking-popup-seen';
    if (sessionStorage.getItem(STORAGE_KEY)) return;

    // ~55% of visits get the timed popup
    if (Math.random() > 0.55) {
      sessionStorage.setItem(STORAGE_KEY, 'skipped');
      return;
    }

    const delayMs = 9000 + Math.floor(Math.random() * 7000); // 9–16s
    const timer = setTimeout(() => {
      if (sessionStorage.getItem(STORAGE_KEY)) return;
      sessionStorage.setItem(STORAGE_KEY, 'opened');
      setSubmitStatus({ type: '', message: '' });
      setBookingStep(0);
      setIsBookingModalOpen(true);
    }, delayMs);

    return () => clearTimeout(timer);
  }, []);

  const openBookingModal = () => {
    sessionStorage.setItem('webanova-booking-popup-seen', 'opened');
    setSubmitStatus({ type: '', message: '' });
    setBookingStep(0);
    setIsBookingModalOpen(true);
  };

  const closeBookingModal = () => {
    sessionStorage.setItem('webanova-booking-popup-seen', 'dismissed');
    setIsBookingModalOpen(false);
    setBookingStep(0);
  };

  useEffect(() => {
    if (!isBookingModalOpen) return;
    stepInputRef.current?.focus();
  }, [bookingStep, isBookingModalOpen]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => {
      const next = { ...prev, [name]: value };

      if (name === 'month' || name === 'year') {
        const maxDay = getDaysInMonth(next.month, next.year);
        if (next.day && parseInt(next.day, 10) > maxDay) {
          next.day = '';
        }
      }

      return next;
    });
  };

  const getFormattedDate = () => {
    const { month, day, year } = formData;
    if (!month || !day || !year) return formData.date || '';
    return `${year}-${month}-${day.padStart(2, '0')}`;
  };

  const isDateSelectionValid = () => {
    const { month, day, year } = formData;
    if (!month || !day || !year) return false;

    const daysInMonth = getDaysInMonth(month, year);
    if (parseInt(day, 10) > daysInMonth) return false;

    const selectedDate = new Date(parseInt(year, 10), parseInt(month, 10) - 1, parseInt(day, 10));
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return selectedDate >= today;
  };

  const isCurrentStepValid = () => {
    const step = bookingSteps[bookingStep];
    if (!step.required) return true;

    if (step.type === 'dateDropdowns') {
      return isDateSelectionValid();
    }

    const value = formData[step.field]?.trim();
    if (!value) return false;
    if (step.type === 'email') {
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
    }
    return true;
  };

  const handleBookingNext = () => {
    if (!isCurrentStepValid()) return;
    if (bookingStep < bookingSteps.length - 1) {
      setBookingStep(prev => prev + 1);
    }
  };

  const handleBookingBack = () => {
    if (bookingStep > 0) {
      setBookingStep(prev => prev - 1);
    }
  };

  const handleConsultationSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus({ type: '', message: '' });

    const fullName = `${formData.firstName} ${formData.lastName}`.trim() || formData.name.trim();

    try {
      const { ok, data } = await submitForm(API_ENDPOINTS.CONSULTATION, {
        type: 'consultation',
        name: fullName,
        email: formData.email,
        phone: formData.phone,
        date: getFormattedDate(),
        time: formData.time,
        message: formData.message,
      });

      if (ok) {
        setSubmitStatus({ type: 'success', message: 'Consultation request submitted successfully! We will contact you soon.' });
        setFormData({
          firstName: '',
          lastName: '',
          name: '',
          email: '',
          phone: '',
          month: '',
          day: '',
          year: '',
          date: '',
          time: '',
          message: ''
        });
        setBookingStep(0);
        setTimeout(() => {
          setIsBookingModalOpen(false);
          setSubmitStatus({ type: '', message: '' });
        }, 2000);
      } else {
        setSubmitStatus({ type: 'error', message: data.error || 'Failed to submit consultation request. Please try again.' });
      }
    } catch (error) {
      setSubmitStatus({ type: 'error', message: 'Something went wrong. Please try again.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderDateDropdownFields = (selectClassName, monthRef = null) => {
    const maxDays = getDaysInMonth(formData.month, formData.year);
    const dayOptions = Array.from({ length: maxDays }, (_, i) => i + 1);
    const yearOptions = getYearOptions();
    const labelStyle = {
      fontFamily: "'Poppins', sans-serif",
      color: '#292929',
      fontSize: '0.95rem',
    };
    const selectStyle = { fontFamily: "'Poppins', sans-serif", color: '#292929' };

    return (
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div>
          <label className="block mb-2" style={labelStyle}>Month *</label>
          <select
            ref={monthRef}
            name="month"
            value={formData.month}
            onChange={handleInputChange}
            required
            className={selectClassName}
            style={selectStyle}
          >
            <option value="">Month</option>
            {MONTH_OPTIONS.map((month) => (
              <option key={month.value} value={month.value}>{month.label}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block mb-2" style={labelStyle}>Day *</label>
          <select
            name="day"
            value={formData.day}
            onChange={handleInputChange}
            required
            className={selectClassName}
            style={selectStyle}
          >
            <option value="">Day</option>
            {dayOptions.map((day) => (
              <option key={day} value={String(day)}>{day}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block mb-2" style={labelStyle}>Year *</label>
          <select
            name="year"
            value={formData.year}
            onChange={handleInputChange}
            required
            className={selectClassName}
            style={selectStyle}
          >
            <option value="">Year</option>
            {yearOptions.map((year) => (
              <option key={year} value={String(year)}>{year}</option>
            ))}
          </select>
        </div>
      </div>
    );
  };

  const renderConsultationForm = () => (
    <>
      {submitStatus.message && (
        <div
          className={`mb-6 p-4 rounded-lg ${
            submitStatus.type === 'success' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
          }`}
          style={{ fontFamily: "'Poppins', sans-serif" }}
        >
          {submitStatus.message}
        </div>
      )}
      <form className="space-y-6" onSubmit={handleConsultationSubmit}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label
              className="block mb-2"
              style={{
                fontFamily: "'Poppins', sans-serif",
                color: '#292929',
                fontSize: '0.95rem'
              }}
            >
              Name *
            </label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              required
              className="w-full px-4 py-3 rounded-lg bg-white/80 backdrop-blur-sm border border-white/40 focus:outline-none focus:ring-2 focus:ring-[#ff7a59] focus:border-transparent transition-all duration-300"
              style={{ fontFamily: "'Poppins', sans-serif", color: '#292929' }}
            />
          </div>
          <div>
            <label
              className="block mb-2"
              style={{
                fontFamily: "'Poppins', sans-serif",
                color: '#292929',
                fontSize: '0.95rem'
              }}
            >
              Email *
            </label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              required
              className="w-full px-4 py-3 rounded-lg bg-white/80 backdrop-blur-sm border border-white/40 focus:outline-none focus:ring-2 focus:ring-[#ff7a59] focus:border-transparent transition-all duration-300"
              style={{ fontFamily: "'Poppins', sans-serif", color: '#292929' }}
            />
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label
              className="block mb-2"
              style={{
                fontFamily: "'Poppins', sans-serif",
                color: '#292929',
                fontSize: '0.95rem'
              }}
            >
              Phone *
            </label>
            <input
              type="tel"
              name="phone"
              value={formData.phone}
              onChange={handleInputChange}
              required
              className="w-full px-4 py-3 rounded-lg bg-white/80 backdrop-blur-sm border border-white/40 focus:outline-none focus:ring-2 focus:ring-[#ff7a59] focus:border-transparent transition-all duration-300"
              style={{ fontFamily: "'Poppins', sans-serif", color: '#292929' }}
            />
          </div>
        </div>
        <div>
          <label
            className="block mb-2"
            style={{
              fontFamily: "'Poppins', sans-serif",
              color: '#292929',
              fontSize: '0.95rem'
            }}
          >
            Preferred Date *
          </label>
          {renderDateDropdownFields(
            "w-full px-4 py-3 rounded-lg bg-white/80 backdrop-blur-sm border border-white/40 focus:outline-none focus:ring-2 focus:ring-[#ff7a59] focus:border-transparent transition-all duration-300"
          )}
        </div>
        <div>
          <label
            className="block mb-2"
            style={{
              fontFamily: "'Poppins', sans-serif",
              color: '#292929',
              fontSize: '0.95rem'
            }}
          >
            Preferred Time *
          </label>
          <select
            name="time"
            value={formData.time}
            onChange={handleInputChange}
            required
            className="w-full px-4 py-3 rounded-lg bg-white/80 backdrop-blur-sm border border-white/40 focus:outline-none focus:ring-2 focus:ring-[#ff7a59] focus:border-transparent transition-all duration-300"
            style={{ fontFamily: "'Poppins', sans-serif", color: '#292929' }}
          >
            <option value="">Select a time</option>
            <option value="9am">9:00 AM</option>
            <option value="10am">10:00 AM</option>
            <option value="11am">11:00 AM</option>
            <option value="12pm">12:00 PM</option>
            <option value="1pm">1:00 PM</option>
            <option value="2pm">2:00 PM</option>
            <option value="3pm">3:00 PM</option>
            <option value="4pm">4:00 PM</option>
            <option value="5pm">5:00 PM</option>
          </select>
        </div>
        <div>
          <label
            className="block mb-2"
            style={{
              fontFamily: "'Poppins', sans-serif",
              color: '#292929',
              fontSize: '0.95rem'
            }}
          >
            Message (Optional)
          </label>
          <textarea
            name="message"
            value={formData.message}
            onChange={handleInputChange}
            rows="4"
            className="w-full px-4 py-3 rounded-lg bg-white/80 backdrop-blur-sm border border-white/40 focus:outline-none focus:ring-2 focus:ring-[#ff7a59] focus:border-transparent resize-none transition-all duration-300"
            style={{ fontFamily: "'Poppins', sans-serif", color: '#292929' }}
            placeholder="Tell us about your project..."
          ></textarea>
        </div>
        <div className="text-center pt-4">
          <button
            type="submit"
            disabled={isSubmitting}
            className="group w-[180px] md:w-[200px] py-3 backdrop-blur-sm text-white font-medium rounded-full relative overflow-hidden transition-all duration-300 shadow-lg transform hover:scale-105 hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed"
            style={{
              backgroundColor: '#ff7a59',
              fontFamily: "'Poppins', sans-serif"
            }}
          >
            <span className="relative z-10 text-sm md:text-base">
              {isSubmitting ? 'SUBMITTING...' : 'BOOK FREE CONSULTATION'}
            </span>
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000"></div>
          </button>
        </div>
      </form>
    </>
  );

  const renderMultiStepBookingForm = () => {
    const currentStep = bookingSteps[bookingStep];
    const isLastStep = bookingStep === bookingSteps.length - 1;
    const isTextarea = currentStep.type === 'textarea';
    const inputClassName = "booking-fun-input relative z-0 w-full py-4 pr-5 text-base md:text-lg rounded-2xl bg-white/90 backdrop-blur-sm border-2 border-white/60 focus:outline-none focus:border-[#ff7a59] focus:bg-white transition-all duration-300 shadow-md hover:shadow-lg hover:border-white";
    const inputStyle = {
      fontFamily: "'Poppins', sans-serif",
      color: '#292929',
      paddingLeft: '3.75rem',
    };

    const handleStepKeyDown = (e) => {
      if (e.key === 'Enter' && !isLastStep) {
        e.preventDefault();
        handleBookingNext();
      }
    };

    const renderStepInput = () => {
      const emojiClass = isTextarea
        ? 'absolute left-4 top-4 z-10 text-2xl pointer-events-none booking-step-emoji'
        : 'absolute left-4 top-1/2 -translate-y-1/2 z-10 text-2xl pointer-events-none booking-step-emoji';

      const dateSelectClassName = "booking-fun-input relative z-0 w-full py-4 px-4 text-base md:text-lg rounded-2xl bg-white/90 backdrop-blur-sm border-2 border-white/60 focus:outline-none focus:border-[#ff7a59] focus:bg-white transition-all duration-300 shadow-md hover:shadow-lg hover:border-white";

      const inputWrapper = (input) => (
        <div key={bookingStep} className="booking-input-pop relative group">
          <div className="absolute -inset-1 rounded-3xl bg-gradient-to-r from-[#ff7a59]/40 via-[#accbff]/60 to-[#ff7a59]/40 opacity-0 group-focus-within:opacity-100 blur-sm transition-opacity duration-300 pointer-events-none z-0" />
          <div className="relative z-0">
            {input}
            <span className={emojiClass} aria-hidden="true">{currentStep.emoji}</span>
          </div>
        </div>
      );

      if (currentStep.type === 'dateDropdowns') {
        return (
          <div key={bookingStep} className="booking-input-pop">
            <div className="text-center text-3xl mb-4" aria-hidden="true">{currentStep.emoji}</div>
            {renderDateDropdownFields(dateSelectClassName, stepInputRef)}
          </div>
        );
      }

      if (currentStep.type === 'select') {
        return inputWrapper(
          <select
            ref={stepInputRef}
            name={currentStep.field}
            value={formData[currentStep.field]}
            onChange={handleInputChange}
            onKeyDown={handleStepKeyDown}
            required={currentStep.required}
            className={inputClassName}
            style={inputStyle}
          >
            <option value="">Pick a time...</option>
            <option value="9am">9:00 AM</option>
            <option value="10am">10:00 AM</option>
            <option value="11am">11:00 AM</option>
            <option value="12pm">12:00 PM</option>
            <option value="1pm">1:00 PM</option>
            <option value="2pm">2:00 PM</option>
            <option value="3pm">3:00 PM</option>
            <option value="4pm">4:00 PM</option>
            <option value="5pm">5:00 PM</option>
          </select>
        );
      }

      if (currentStep.type === 'textarea') {
        return inputWrapper(
          <textarea
            ref={stepInputRef}
            name={currentStep.field}
            value={formData[currentStep.field]}
            onChange={handleInputChange}
            rows="4"
            className={`${inputClassName} resize-none`}
            style={{ ...inputStyle, paddingTop: '1rem' }}
            placeholder={currentStep.placeholder}
          />
        );
      }

      return inputWrapper(
        <input
          ref={stepInputRef}
          type={currentStep.type}
          name={currentStep.field}
          value={formData[currentStep.field]}
          onChange={handleInputChange}
          onKeyDown={handleStepKeyDown}
          required={currentStep.required}
          className={inputClassName}
          style={inputStyle}
          placeholder={currentStep.placeholder}
        />
      );
    };

    return (
      <>
        {submitStatus.message && (
          <div
            className={`mb-6 p-4 rounded-lg ${
              submitStatus.type === 'success' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
            }`}
            style={{ fontFamily: "'Poppins', sans-serif" }}
          >
            {submitStatus.message}
          </div>
        )}

        <div className="mb-8">
          <div className="flex justify-center gap-2 mb-6">
            {bookingSteps.map((_, index) => (
              <div
                key={index}
                className={`h-2 rounded-full transition-all duration-300 ${
                  index === bookingStep ? 'w-8 bg-[#ff7a59]' : index < bookingStep ? 'w-2 bg-[#ff7a59]/60' : 'w-2 bg-white/40'
                }`}
              />
            ))}
          </div>
          <p
            className="text-center text-xs uppercase tracking-[0.2em] mb-3"
            style={{ fontFamily: "'Poppins', sans-serif", color: '#ff7a59', fontWeight: 600 }}
          >
            Step {bookingStep + 1} of {bookingSteps.length}
          </p>
          <h3
            className="text-xl md:text-2xl font-bold text-center mb-6"
            style={{
              fontFamily: "'Poppins', sans-serif",
              color: '#292929',
            }}
          >
            {currentStep.label}
          </h3>
        </div>

        <form
          className="space-y-6"
          onSubmit={isLastStep ? handleConsultationSubmit : (e) => {
            e.preventDefault();
            handleBookingNext();
          }}
        >
          <div>{renderStepInput()}</div>

          <div className="flex flex-wrap items-center justify-between gap-4 pt-4">
            <button
              type="button"
              onClick={handleBookingBack}
              disabled={bookingStep === 0}
              className="px-6 py-3 rounded-full border border-white/40 text-gray-700 font-medium transition-all duration-300 hover:border-[#ff7a59] hover:text-[#ff7a59] disabled:opacity-0 disabled:pointer-events-none"
              style={{ fontFamily: "'Poppins', sans-serif" }}
            >
              Back
            </button>

            {isLastStep ? (
              <button
                type="submit"
                disabled={isSubmitting}
                className="group ml-auto w-[180px] md:w-[200px] py-3 backdrop-blur-sm text-white font-medium rounded-full relative overflow-hidden transition-all duration-300 shadow-lg transform hover:scale-105 hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed"
                style={{
                  backgroundColor: '#ff7a59',
                  fontFamily: "'Poppins', sans-serif"
                }}
              >
                <span className="relative z-10 text-sm md:text-base">
                  {isSubmitting ? 'SUBMITTING...' : 'BOOK CONSULTATION'}
                </span>
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000"></div>
              </button>
            ) : (
              <button
                type="submit"
                disabled={!isCurrentStepValid()}
                className="group ml-auto w-[140px] md:w-[160px] py-3 backdrop-blur-sm text-white font-medium rounded-full relative overflow-hidden transition-all duration-300 shadow-lg transform hover:scale-105 hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed"
                style={{
                  backgroundColor: '#ff7a59',
                  fontFamily: "'Poppins', sans-serif"
                }}
              >
                <span className="relative z-10 text-sm md:text-base">Next</span>
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000"></div>
              </button>
            )}
          </div>
        </form>
      </>
    );
  };

  return (
    <div className="relative min-h-screen">
      <Navbar />

      {/* Booking Modal */}
      {isBookingModalOpen && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center p-4"
          onClick={closeBookingModal}
        >
          <div className="absolute inset-0 bg-[#292929]/40 backdrop-blur-sm" />
          <div
            className="relative w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-2xl shadow-2xl border border-white/20 backdrop-blur-sm"
            style={{ backgroundColor: '#accbff' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="absolute inset-0 rounded-2xl bg-white/10 pointer-events-none" />
            <div className="relative p-6 md:p-10">
              <button
                type="button"
                onClick={closeBookingModal}
                className="absolute top-4 right-4 text-gray-700 hover:text-[#ff7a59] transition-colors"
                aria-label="Close booking form"
              >
                <FaTimes className="w-5 h-5" />
              </button>

              <div className="text-center mb-8 pr-8">
                <h2
                  className="text-2xl md:text-3xl font-bold mb-3 text-gray-700"
                  style={sectionHeadingStyle}
                >
                  FREE CONSULTATION!
                </h2>
                <p
                  className="text-sm md:text-base"
                  style={{
                    fontFamily: "'Poppins', sans-serif",
                    color: '#292929',
                  }}
                >
                  Schedule a free consultation to discuss your website needs
                </p>
              </div>

              <BookingForm onSuccess={closeBookingModal} />
            </div>
          </div>
        </div>
      )}
      
      {/* Background */}
      <div className="fixed top-0 left-0 w-full h-full -z-10" style={{ backgroundColor: '#accbff' }} />

      {/* Top section — stays pinned while next section rolls over */}
      <div className="sticky top-0 z-0 h-screen overflow-hidden" style={{ backgroundColor: '#accbff' }}>
        {/* Metal icon visual */}
        <div
          className="pointer-events-none absolute z-0 animate-fade-in"
          aria-hidden="true"
          style={{ left: 'clamp(-40px, -2vw, 8px)', bottom: 'clamp(-80px, -8vh, -36px)' }}
        >
          <img
            src="/inspoArt/METALICON1-removebg-preview.png"
            alt=""
            className="hero-metal-icon select-none"
            style={{
              width: 'min(58vw, 420px)',
              height: 'auto',
              maxHeight: '52vh',
              objectFit: 'contain',
              opacity: 0.95,
              mixBlendMode: 'lighten',
              filter: 'drop-shadow(0 24px 48px rgba(41, 41, 41, 0.22))',
            }}
          />
        </div>

        {/* Metal icon 2 — top right */}
        <div
          className="pointer-events-none absolute z-0 animate-fade-in"
          aria-hidden="true"
          style={{
            top: 'clamp(88px, 14vh, 128px)',
            right: 'clamp(-24px, -1vw, 12px)',
          }}
        >
          <img
            src="/inspoArt/METALICON2-removebg-preview.png"
            alt=""
            className="hero-metal-icon select-none"
            style={{
              width: 'min(42vw, 280px)',
              height: 'auto',
              maxHeight: '36vh',
              objectFit: 'contain',
              opacity: 0.95,
              mixBlendMode: 'lighten',
              filter: 'drop-shadow(0 18px 36px rgba(41, 41, 41, 0.2))',
              animationDelay: '0.8s',
            }}
          />
        </div>

        {/* Main Content */}
        <div className="absolute inset-0 flex items-center justify-center px-4">
          <div className="relative z-10 text-center space-y-6">
            {/* Logo */}
            <div className="animate-fade-in-up flex justify-center">
              <img 
                src="/logoTransparent.png" 
                alt="WebaNova Creative Studio" 
                className="mx-auto max-w-full h-auto"
                style={{ maxHeight: 'clamp(120px, 20vw, 220px)', width: 'auto' }}
              />
            </div>

            {/* Description */}
            <p className="text-gray-700 text-sm sm:text-base md:text-lg max-w-xl mx-auto leading-relaxed animate-fade-in-up animate-delay-400" style={{ fontFamily: "'Poppins', sans-serif" }}>
              We don't just build <i><b>websites</b></i>, we build <i><b>systems</b></i> to generate leads and clients so you don't have to.
            </p>

            {/* Buttons */}
            <div className="flex flex-wrap justify-center gap-4 animate-fade-in-up animate-delay-600">
              <Link to="/services" className="group">
                <button className="w-[180px] md:w-[200px] py-3 backdrop-blur-sm text-white font-medium rounded-full relative overflow-hidden transition-all duration-300 shadow-lg transform hover:scale-105 hover:shadow-xl" style={{ backgroundColor: '#ff7a59' }}>
                  <span className="relative z-10 text-sm md:text-base" style={{ fontFamily: "'Poppins', sans-serif" }}>SEE OUR SERVICES</span>
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000"></div>
                </button>
              </Link>
              <button
                type="button"
                onClick={openBookingModal}
                className="group w-[180px] md:w-[200px] py-3 backdrop-blur-sm text-white font-medium rounded-full relative overflow-hidden transition-all duration-300 shadow-lg transform hover:scale-105 hover:shadow-xl"
                style={{ backgroundColor: '#ff7a59' }}
              >
                <span className="relative z-10 text-sm md:text-base" style={{ fontFamily: "'Poppins', sans-serif" }}>FREE CONSULTATION!</span>
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000"></div>
              </button>
            </div>
          </div>
        </div>

        {/* Scroll Down Indicator */}
        <div 
          className={`absolute bottom-8 left-1/2 transform -translate-x-1/2 flex flex-col items-center transition-opacity duration-500 ${
            showScrollIndicator ? 'opacity-100' : 'opacity-0 pointer-events-none'
          }`}
        >
          <span className="text-white font-playfair text-sm mb-2">Scroll Down</span>
          <div className="w-6 h-10 border-2 border-white rounded-full p-1">
            <div className="w-1.5 h-1.5 bg-white rounded-full mx-auto animate-bounce"></div>
          </div>
        </div>
      </div>

      {/* Content rolls up over the pinned hero */}
      <div
        className="relative z-10"
        style={{
          backgroundColor: '#accbff',
          boxShadow: '0 -24px 48px rgba(41, 41, 41, 0.12)',
        }}
      >
      {/* Services Section */}
      <div id="services-section" className="relative py-20 px-4 md:px-8">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-14">
            <h2
              className="text-3xl md:text-4xl font-bold mb-5 text-gray-700"
              style={sectionHeadingStyle}
            >
              Services
            </h2>
            <p
              className="text-base md:text-lg max-w-2xl mx-auto text-gray-700"
              style={{ fontFamily: "'Poppins', sans-serif" }}
            >
              Pick what you need. We build the system that brings customers in and helps you close them.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8 mb-12 pt-3">
            {services.map((service, index) => (
              <ServiceFlipCard
                key={service.title}
                icon={service.icon}
                title={service.title}
                tagline={service.tagline}
                price={service.price}
                outcome={service.outcome}
                description={service.description}
                highlights={service.highlights}
                badge={service.badge}
                seeMoreLink={service.seeMoreLink}
                className="animate-fade-in-up"
                style={{ animationDelay: `${index * 100}ms` }}
              />
            ))}
          </div>

          <div className="text-center flex flex-col items-center gap-4">
            <Link to="/services" className="group">
              <button
                className="w-[200px] md:w-[220px] py-3 backdrop-blur-sm text-white font-medium rounded-full relative overflow-hidden transition-all duration-300 shadow-lg transform hover:scale-105 hover:shadow-xl"
                style={{ backgroundColor: '#ff7a59' }}
              >
                <span className="relative z-10 text-sm md:text-base" style={{ fontFamily: "'Poppins', sans-serif" }}>
                  EXPLORE ALL SERVICES
                </span>
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000" />
              </button>
            </Link>
            <Link to="/offers" className="group">
              <button
                className="w-[220px] md:w-[260px] py-3 font-medium rounded-full relative overflow-hidden transition-all duration-300 shadow-md transform hover:scale-105 hover:shadow-lg border-2 border-[#ff7a59] bg-white/40 backdrop-blur-sm text-gray-700"
                style={{ fontFamily: "'Poppins', sans-serif" }}
              >
                <span className="relative z-10 text-sm md:text-base">
                  EXCLUSIVE OFFERS AND DEALS
                </span>
              </button>
            </Link>
          </div>
        </div>
      </div>

      {/* Portfolio Section */}
      <div id="our-work-section" className="relative py-20 px-4 md:px-8">
        <div className="max-w-5xl mx-auto">
          <div className="mb-16">
            <div className="h-[2px] bg-gray-400/60 w-full"></div>
          </div>

          <div className="text-center mb-12">
            <h2
              className="text-3xl md:text-4xl font-bold mb-6 text-gray-700"
              style={sectionHeadingStyle}
            >
              Some of our work
            </h2>
            <p
              className="text-base md:text-lg"
              style={{ fontFamily: "'Poppins', sans-serif", color: '#292929' }}
            >
              Tap a frame to watch a preview
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8 mb-12">
            {portfolioVideos.map((item, index) => (
              <div key={item.title} className="flex flex-col">
                <button
                  type="button"
                  onClick={() => handleVideoClick(index)}
                  className="relative aspect-video rounded-2xl overflow-hidden bg-white/10 backdrop-blur-sm border border-white/20 shadow-xl text-left group focus:outline-none focus:ring-2 focus:ring-[#ff7a59] focus:ring-offset-2 focus:ring-offset-[#accbff]"
                >
                  <video
                    ref={(el) => { videoRefs.current[index] = el; }}
                    src={item.videoSrc}
                    poster={item.poster}
                    className="w-full h-full object-cover"
                    playsInline
                    muted
                    controls={activeVideoIndex === index}
                    onClick={(e) => e.stopPropagation()}
                    onEnded={() => setActiveVideoIndex(null)}
                  />
                  {activeVideoIndex !== index && (
                    <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/25 group-hover:bg-black/35 transition-colors">
                      <div className="w-14 h-14 md:w-16 md:h-16 rounded-full bg-white/90 flex items-center justify-center shadow-lg transform group-hover:scale-105 transition-transform">
                        <svg className="w-6 h-6 md:w-7 md:h-7 text-[#ff7a59] ml-1" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M8 5v14l11-7z" />
                        </svg>
                      </div>
                      <p
                        className="mt-3 text-white text-sm md:text-base font-medium drop-shadow-md"
                        style={{ fontFamily: "'Poppins', sans-serif" }}
                      >
                        {item.title}
                      </p>
                    </div>
                  )}
                </button>
                {item.description && item.liveUrl && (
                  <p
                    className="mt-4 text-sm md:text-base text-center leading-relaxed"
                    style={{ fontFamily: "'Poppins', sans-serif", color: '#292929' }}
                  >
                    {item.description}{' '}
                    <a
                      href={item.liveUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="font-semibold underline underline-offset-2 hover:opacity-80 transition-opacity"
                      style={{ color: '#ff7a59' }}
                    >
                      {item.liveLabel || 'View live site'}
                    </a>
                  </p>
                )}
              </div>
            ))}
          </div>

          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/contactme" className="group">
              <button
                className="w-[180px] md:w-[200px] py-3 backdrop-blur-sm text-white font-medium rounded-full relative overflow-hidden transition-all duration-300 shadow-lg transform hover:scale-105 hover:shadow-xl"
                style={{ backgroundColor: '#ff7a59' }}
              >
                <span className="relative z-10 text-sm md:text-base" style={{ fontFamily: "'Poppins', sans-serif" }}>CONTACT</span>
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000"></div>
              </button>
            </Link>
            <a href="https://forms.fillout.com/t/7dtzNv3Hv7us" target="_blank" rel="noopener noreferrer" className="group">
              <button
                className="w-[180px] md:w-[220px] py-3 backdrop-blur-sm text-white font-medium rounded-full relative overflow-hidden transition-all duration-300 shadow-lg transform hover:scale-105 hover:shadow-xl"
                style={{ backgroundColor: '#ff7a59' }}
              >
                <span className="relative z-10 text-sm md:text-base" style={{ fontFamily: "'Poppins', sans-serif" }}>GET A WEBSITE</span>
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000"></div>
              </button>
            </a>
          </div>
        </div>
      </div>

      {/* Accessibility / ADA Section */}
      <div id="accessibility-section" className="relative py-20 px-4 md:px-8">
        <div className="max-w-6xl mx-auto">
          <div className="mb-16">
            <div className="h-[2px] bg-gray-400/60 w-full"></div>
          </div>

          <div className="text-center mb-14">
            <p
              className="text-xs uppercase tracking-wider mb-3 font-semibold"
              style={{ fontFamily: "'Poppins', sans-serif", color: '#ff7a59' }}
            >
              Accessibility
            </p>
            <h2
              className="text-3xl md:text-4xl font-bold mb-5 text-gray-700"
              style={sectionHeadingStyle}
            >
              Accessibility shaped around your website
            </h2>
            <p
              className="text-base md:text-lg max-w-2xl mx-auto text-gray-700 mb-5"
              style={{ fontFamily: "'Poppins', sans-serif" }}
            >
              Anyone should be able to use your site. We build for that, and add tools visitors can adjust.
            </p>
            <div
              className="inline-flex flex-col sm:flex-row items-center gap-2 sm:gap-3 rounded-2xl px-5 py-3.5 shadow-md border"
              style={{
                backgroundColor: 'rgba(255, 252, 240, 0.9)',
                borderColor: 'rgba(255, 122, 89, 0.45)',
              }}
            >
              <span
                className="shrink-0 text-xs font-bold uppercase tracking-wide px-3 py-1.5 rounded-full text-white"
                style={{ backgroundColor: '#ff7a59', fontFamily: "'Poppins', sans-serif" }}
              >
                Included
              </span>
              <p
                className="text-sm md:text-base font-medium text-center sm:text-left"
                style={{ fontFamily: "'Poppins', sans-serif", color: '#292929' }}
              >
                Buy a website with us, and ADA features come with it.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8 mb-12">
            {[
              {
                icon: FaUniversalAccess,
                title: 'Aligned with ADA standards',
                body: 'WCAG 2.2 AA minded fixes that help your site meet U.S. accessibility expectations and cut legal risk.',
              },
              {
                icon: FaSlidersH,
                title: 'Tuned to your site',
                body: 'Options and plans that scale with you, whether you run a local shop site or a multi-page brand hub.',
              },
              {
                icon: FaHandsHelping,
                title: 'We carry the load',
                body: 'Fast to launch. We handle setup, tuning, and the ongoing details so you can stay focused on clients.',
              },
            ].map((item, index) => (
              <div
                key={item.title}
                className="ada-teaser-card rounded-2xl border shadow-md p-6 md:p-8 text-center animate-fade-in-up"
                style={{
                  animationDelay: `${index * 100}ms`,
                  backgroundColor: 'rgba(255, 252, 240, 0.72)',
                  borderColor: 'rgba(255, 122, 89, 0.35)',
                  backdropFilter: 'blur(8px)',
                }}
              >
                <div
                  className="ada-teaser-icon w-14 h-14 mx-auto mb-5 rounded-xl flex items-center justify-center"
                  style={{ backgroundColor: '#ff7a59' }}
                >
                  <item.icon className="w-7 h-7 text-white" />
                </div>
                <h3
                  className="text-lg md:text-xl font-bold mb-3 text-gray-700"
                  style={{ fontFamily: "'Poppins', sans-serif" }}
                >
                  {item.title}
                </h3>
                <p
                  className="text-sm md:text-base leading-relaxed"
                  style={{ fontFamily: "'Poppins', sans-serif", color: '#292929' }}
                >
                  {item.body}
                </p>
              </div>
            ))}
          </div>

          <div className="text-center">
            <Link to="/accessibility" className="group">
              <button
                className="w-[220px] md:w-[260px] py-3 backdrop-blur-sm text-white font-medium rounded-full relative overflow-hidden transition-all duration-300 shadow-lg transform hover:scale-105 hover:shadow-xl"
                style={{ backgroundColor: '#ff7a59' }}
              >
                <span className="relative z-10 text-sm md:text-base" style={{ fontFamily: "'Poppins', sans-serif" }}>
                  LEARN ABOUT ADA
                </span>
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000"></div>
              </button>
            </Link>
          </div>
        </div>
      </div>
      
      {/* Booking Section */}
      <div id="booking-section" className="relative min-h-screen py-20 px-4 md:px-8">
        <div className="max-w-4xl mx-auto">
          {/* Divider */}
          <div className="mb-16">
            <div className="h-[2px] bg-gray-400/60 w-full"></div>
          </div>
          {/* Header */}
          <div className="text-center mb-12">
            <h2 
              className="text-3xl md:text-4xl font-bold mb-6 text-gray-700"
              style={sectionHeadingStyle}
            >
              Book Your Free Consultation
            </h2>
            <p 
              className="text-base md:text-lg"
              style={{ 
                fontFamily: "'Poppins', sans-serif",
                color: '#292929'
              }}
            >
              Schedule a free consultation with us to discuss your website needs
            </p>
          </div>

          {/* Booking Form Card */}
          <div className="bg-white/10 backdrop-blur-sm p-8 md:p-12 rounded-2xl shadow-xl border border-white/20">
            {renderConsultationForm()}
          </div>
        </div>
      </div>

      <Footer />
      </div>
    </div>
  );
} 